import { PropsWithChildren } from 'react'
import { MemberBadge } from 'src/features/shared/components/MemberBadge'
import { Schemas } from '~/api/gen'
import { Img } from '~/components/elements/Img'
import fallbackIcon from '~/images/user_80.gif'

const MemberImage: React.FC<{
  imageUrl?: string
  linkUrl?: string
  linkLabel?: string
}> = ({ imageUrl, linkUrl, linkLabel }) => {
  return (
    <a
      href={linkUrl}
      className="size-12 overflow-hidden rounded-[50%] border border-gray-300 hover:opacity-70"
      aria-label={linkLabel}
    >
      <Img
        src={imageUrl ? imageUrl : fallbackIcon}
        width={48}
        height={48}
        alt=""
      />
    </a>
  )
}

type Member = Pick<
  Schemas.member_large,
  | 'nick_name'
  | 'sex'
  | 'height'
  | 'hair_style'
  | 'age'
  | 'is_age_published'
  | 'image'
  | 'is_salon_clerk'
  | 'is_shop_clerk'
  | 'is_sponsored'
  | 'is_wearista'
  | 'is_awarded'
>

const MemberInformation: React.FC<{
  member: Member
}> = ({ member }) => {
  const displayAge = member.is_age_published ? member.age : undefined
  return (
    <div className="items-center pt-[2px] leading-[1.2]">
      <p className="flex items-center gap-x-1 text-[20px]">
        {member.nick_name}
        <MemberBadge className="inline-block size-[18px]" member={member} />
      </p>
      {(member.height || member.sex || displayAge || member.hair_style) && (
        <p className="py-1 text-[13px]">
          {[
            member.height && `${member.height}cm`,
            member.sex?.name,
            displayAge && `${displayAge}歳`,
            member.hair_style?.name,
          ]
            .filter(Boolean)
            .join(' / ')}
        </p>
      )}
    </div>
  )
}

export type Props = PropsWithChildren<{
  member?: Member
  hasBottomShadow?: boolean
  linkUrl?: string
  linkLabel?: string
}>

export const MemberHeaderForPc: React.FC<Props> = ({
  member,
  hasBottomShadow = false,
  linkUrl,
  linkLabel,
  children,
}) => {
  return (
    <div
      className={`w-full bg-white/[0.97] pb-[14px] pt-[11px] ${
        hasBottomShadow
          ? 'border-t shadow-[0_4px_6px_-2px_rgba(0,0,0,0.3)]'
          : 'border-b'
      } border-gray-300`}
    >
      <div className="mx-auto flex w-[990px] items-center justify-between">
        <div className="flex items-center gap-x-[10px]">
          <MemberImage
            imageUrl={member?.image.url_80}
            linkUrl={linkUrl}
            linkLabel={linkLabel}
          />
          {member !== undefined && <MemberInformation member={member} />}
        </div>
        {children}
      </div>
    </div>
  )
}
